import { ArrayUtils, engineComponent, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { Payline } from "./Payline";
import { PaylinesDefinitions } from "./PaylinesDefinitions";
import { Symbol } from "./Symbol";
import { Symbols } from "./Symbols";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Configuration } from "../Components/Configuration";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { Languages } from "../Utils/Languages";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { AppContext } from "../../Common/AppContext";
import { PlayList } from "../Components/Sounds/PlayList";
import { ModuleManager } from "../Managers/ModuleManager";
import { PaylinesExtended } from "../CustomComponents/PaylinesExtended";
import { LayoutContainerElement } from "../CustomComponents/LayoutContainerElement";

/**
 * WinAnimator uses paylines and symbols to create presentation of wins to user.
 * Also handle high wins animation for presentation special screens to user when win is bigger than some defined values.
 * @class WinAnimator
 * @extends ContainerElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimator extends LayoutContainerElement {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly FinishWinCalculation: string = "eventFinishWinCalculation";

    public static readonly FinishWinAnimator: string = "eventFinishWinAnimator";

    //******************************************************************************************************************
    //  TIMER STATUSES
    //******************************************************************************************************************

    protected static readonly Idle: string = "idle";

    protected static readonly CycleWins: string = "cycleWins";

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected reelCount: number;
    protected reelX: Array<number>;
    protected reelY: Array<number>;

    protected symbolCount: number;
    protected symbolWidth: number;
    protected symbolHeight: number;

    protected timerStatus: string;
    protected timer: FrameTimerEventEmitter;

    protected symbols: Symbols;
    protected noWinSymbolsContainer: ContainerElement;
    protected symbolsContainer: ContainerElement;
    public currentSymbols: Array<Array<Symbol>>;

    protected paylines: PaylinesExtended;

    protected multipliers: Array<number>;
    protected currentMultiplierIndex: number;

    protected animationNames: Array<string>;
    protected animationsContainer: ContainerElement;
    protected animations: Array<TimelineElement>;
    protected currentAnimationIndex: number;
    protected currentAnimationFinished: boolean;
    protected value: TimelineElement;

    protected currentReels: Array<Communication.Game.IReel>;
    protected currentWins: Array<Communication.Game.IWin>;
    protected currentWin: number;

    protected currentCycle: number;
    protected cycleDate: number;
    protected cycleStep: number;

    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    public create(node: Element): void {
        super.create(node);

        if (node.hasAttribute(WinAnimatorAttributes.CONFIGS)) {
            let configs: Array<string> = node.getAttribute(WinAnimatorAttributes.CONFIGS).split(";");

            for (let i = 0; i < configs.length; i++) {
                this.configure(Configuration.Configurations[configs[i]]);
            }
        }
    }

    protected configure(node: Element): void {
        if (node.hasAttribute("ReelCount")) {
            this.reelCount = parseInt(node.getAttribute("ReelCount"));
        }

        if (node.hasAttribute("ReelX")) {
            this.reelX = ArrayUtils.numberArray(node.getAttribute("ReelX"));
        }

        if (node.hasAttribute("ReelY")) {
            this.reelY = ArrayUtils.numberArray(node.getAttribute("ReelY"));
        }

        if (node.hasAttribute("VisibleSymbolCount")) {
            this.symbolCount = parseInt(node.getAttribute("VisibleSymbolCount"));
        }

        if (node.hasAttribute("SymbolWidth")) {
            this.symbolWidth = parseInt(node.getAttribute("SymbolWidth"));
        }

        if (node.hasAttribute("SymbolHeight")) {
            this.symbolHeight = parseInt(node.getAttribute("SymbolHeight"));
        }

        if (node.hasAttribute("WinsMultipliers")) {
            this.multipliers = ArrayUtils.numberArray(node.getAttribute("WinsMultipliers"));
        }

        if (node.hasAttribute("WinsAnimations")) {
            this.animationNames = node.getAttribute("WinsAnimations").split(";");
        }
    }

    public initialize(
        symbols: Symbols,
        paylines?: PaylinesExtended,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
    ): void {
        this.createTimer();
        this.createSymbols(symbols, noWinSymbolsContainer, symbolsContainer);
        this.createPaylines(paylines);
        this.createAnimations();
    }

    //******************************************************************************************************************
    //  ON REEL STOPPED, SHOW SYMBOLS
    //******************************************************************************************************************

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        this.currentReels = reelsData;

        for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
            this.removeCurrentSymbol(reelIndex, i);
        }

        // for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
        for (let i = reelsData[reelIndex].symbols.length - 1; i >= 0; i--) {
            this.addCurrentSymbol(reelIndex, i);
        }
    }

    public showSymbols(reelsData: Array<Communication.Game.IReel>): void {
        this.currentReels = reelsData;

        for (let i = 0; i < reelsData.length; i++) {
            for (let j = 0; j < reelsData[i].symbols.length; j++) {
                this.removeCurrentSymbol(i, j);
            }
        }

        for (let i = 0; i < reelsData.length; i++) {
            for (let j = reelsData[i].symbols.length - 1; j >= 0; j--) {
                this.addCurrentSymbol(i, j);
            }
        }
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            if (!this.showCustomWin()) {
                this.showFirstWins();
            }

            return this.currentMultiplierIndex >= 3;
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());

            return false;
        }
    }

    protected showCustomWin(): boolean {
        return false;
    }

    protected showFirstWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            this.winPaylineGoToCase(i, Payline.CaseWin);
        }

        this.startAnimation();
    }

    protected finishFirstWins(): void {
        this.emit(WinAnimator.FinishWinAnimator);

        this.currentCycle = 0;

        this.cycleWins(true);
    }

    //******************************************************************************************************************
    //  SHOW INIT WINS
    //******************************************************************************************************************

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.showCustomInitWin();
            this.showFirstInitWins();
            this.finishFirstInitWins();
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }
    }

    protected showCustomInitWin(): void {}

    protected showFirstInitWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            this.winPaylineGoToCase(i, Payline.CaseWin);
        }
        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
        }
    }

    protected finishFirstInitWins(): void {
        this.currentCycle = 0;

        this.cycleWins(true);
    }

    //******************************************************************************************************************
    //  CYCLE WINS
    //******************************************************************************************************************

    protected cycleWins(isFirst: boolean = false): void {
        if (this.currentWins == null) {
            return;
        }

        if (isFirst) {
            this.cycleStep = 0;
        } else {
            this.cycleStep++;
        }

        PlayList.play("SND_BaseWinline");

        if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive() && this.cycleStep % 4 === 0) {
            this.cycleStep = 0;
            // PlayList.stop('SND_BaseRecapLoop');
            PlayList.loop("SND_BaseRecapLoop");
        }

        this.allPaylinesGoToCase(Payline.CaseNowin);

        if (this.currentCycle === 0) {
            for (let i = 0; i < this.currentWins.length; i++) {
                this.winPaylineGoToCase(i, Payline.CaseNormal);
                this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
            }
        } else {
            for (let i = 0; i < this.currentWins.length; i++) {
                this.winPaylineGoToCase(i, Payline.CaseNormal);
                this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
            }
        }

        if (this.currentWins[this.currentCycle].type == Communication.Game.WinType.freeSpins) {
            this.currentCycle++;
        }

        if (this.currentCycle < this.currentWins.length) {
            this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
            this.winPaylineGoToCase(this.currentCycle, Payline.CaseCycle);
        }

        this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;

        this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelayWithCorrection(isFirst));
    }

    protected getCycleWinsDelayWithCorrection(isFirst: boolean): number {
        let winDelay = (this.getCycleWinsDelay() / 60) * 1000; // in ms
        let diff = (Date.now() - this.cycleDate) % winDelay;
        if (!this.cycleDate || diff > 100 || isFirst) {
            diff = 0;
            this.cycleDate = Date.now();
        }

        return (winDelay - diff) / 16.7;
    }

    protected getCycleWinsDelay(): number {
        return 120;
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin = false): boolean {
        this.stopTimer();

        this.allSymbolsGoToCase(showLastSymbols ? Symbol.CaseNormal : Symbol.CaseEmpty, false);

        this.allPaylinesGoToCase(Payline.CaseNormal);

        if (showLastSymbols && !spin) {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }

        this.value.visible = false;

        let lastMultiplierIndex = this.currentMultiplierIndex;
        this.currentMultiplierIndex = -1;

        if (this.currentAnimationIndex != null) {
            if (this.currentAnimationFinished) {
                this.resetAnimations();

                this.currentWins = null;
                this.currentWin = null;
                return false;
            } else {
                this.stopAnimation();

                this.currentWins = null;
                this.currentWin = null;
                return lastMultiplierIndex >= 2; // condition should be greater than or equals 2 beacause we`ve got only two types of base wins (before high wins)
            }
        } else {
            this.currentWins = null;
            this.currentWin = null;
            //return (lastMultiplierIndex >= 2); // condition should be greater than or equals 2 beacause we`ve got only two types of base wins (before high wins)
            return false;
        }
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected createTimer(): void {
        this.timerStatus = "";

        this.timer = CreateFrameTimer("timer");
        this.timer.on(FrameTimerEvents.Complete, this.onTimerComplete.bind(this));
    }

    protected startTimer(status: string, duration: number): void {
        this.timerStatus = status;

        this.timer.start(duration);
    }

    protected stopTimer(): void {
        if (this.timer != null) {
            this.timer.stop();
        }
    }

    public resetTimer(): void {
        this.stopTimer();

        if (!this.currentAnimationFinished) {
            this.stopAnimation();
        }
    }

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimator.Idle:
                this.idle();
                break;
            case WinAnimator.CycleWins:
                this.cycleWins();
                break;
        }
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected createSymbols(
        symbols: Symbols,
        noWinSymbolsContainer: ContainerElement,
        symbolsContainer: ContainerElement,
    ): void {
        this.symbols = symbols;
        if (noWinSymbolsContainer != null) {
            this.noWinSymbolsContainer = noWinSymbolsContainer;
        } else {
            this.noWinSymbolsContainer = new ContainerElement();
            this.noWinSymbolsContainer.name = "NoWinSymbols";

            this.addChild(this.noWinSymbolsContainer);
        }

        if (symbolsContainer != null) {
            this.symbolsContainer = symbolsContainer;
        } else {
            this.symbolsContainer = new ContainerElement();
            this.symbolsContainer.name = "Symbols";

            this.addChild(this.symbolsContainer);
        }

        this.currentSymbols = [];

        for (let i = 0; i < this.reelCount; i++) {
            this.currentSymbols.push([]);

            for (let j = 0; j < this.symbolCount; j++) {
                this.currentSymbols[i].push(null);
            }
        }
    }

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        if (this.currentSymbols[reelIndex][symbolIndex] === null) {
            const id = this.currentReels[reelIndex].symbols[symbolIndex];

            const symbol = this.symbols.getSymbol(id - 1, Symbol.CaseNormal);
            symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
            symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

            this.symbolsContainer.addChild(symbol);

            this.currentSymbols[reelIndex][symbolIndex] = symbol;
        }
    }

    protected removeCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        if (this.currentSymbols[reelIndex][symbolIndex] !== null) {
            this.symbols.returnSymbol(this.currentSymbols[reelIndex][symbolIndex]);

            this.currentSymbols[reelIndex][symbolIndex] = null;
        }
    }

    protected allSymbolsGoToReset(): void {
        const caseType = Symbol.CaseNormal;

        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = this.currentSymbols[i].length - 1; j >= 0; j--) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    const currentCaseType = symbol.getCurrentCase();

                    if (caseType !== currentCaseType && currentCaseType.indexOf(Symbol.CaseIdle) === -1) {
                        symbol.goToCase(caseType);
                    }

                    this.symbolsContainer.addChild(symbol);
                }
            }
        }
    }

    protected allSymbolsGoToCase(caseType: string, noWin: boolean): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = this.currentSymbols[i].length - 1; j >= 0; j--) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    symbol.goToCase(caseType);

                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                    }
                }
            }
        }
    }

    protected winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean): Array<Symbol> {
        const win = this.currentWins[winIndex];
        let symbols: Array<Symbol> = [];

        switch (win.trigger) {
            case Communication.Game.WinTrigger.Payline:
                symbols = this.winSymbolsGoToCaseAsPayline(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Scatter:
                symbols = this.winSymbolsGoToCaseAsScatter(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Way:
                symbols = this.winSymbolsGoToCaseAsWay(winIndex, caseType);
                break;
            default:
                symbols = this.winSymbolsGoToCaseAsCustom(winIndex, caseType);
                break;
        }

        if (useValue && symbols.length > 0 && win.amount > 0) {
            this.value.visible = true;
            this.value.position.x = symbols[0].position.x + symbols[0].offsetX;
            this.value.position.y = symbols[0].position.y + symbols[0].offsetY;

            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                this.value.play(parseInt(NumberUtils.devide(win.amount, BetManager.CoinSize).toString()));
            } else {
                ImageTextCounterElement.Round = false;
                this.value.play(parseFloat(Languages.formatText(Languages.FormatMoney, win.amount.toString())));
            }
        } else {
            this.value.visible = false;
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsPayline(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        if (this.paylines != null) {
            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                this.paylines.paylineDefinition,
            );

            for (let i = 0; i < definition.length; i++) {
                if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                    const symbol = this.currentSymbols[i][definition[i]];
                    symbol.goToCase(caseType);

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];
                    symbol.goToCase(caseType);

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsWay(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];
                    symbol.goToCase(caseType);

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsCustom(winIndex: number, caseType: string): Array<Symbol> {
        // TODO Implement feature when needed.
        return [];
    }

    protected allowSymbolGoToCase(reelIndex: number, symbolIndex: number, caseType: string): boolean {
        return true;
    }

    //******************************************************************************************************************
    //  PAYLINES
    //******************************************************************************************************************

    protected createPaylines(paylines: PaylinesExtended): void {
        this.paylines = paylines;

        this.allPaylinesGoToCase(Payline.CaseNormal);
    }

    protected allPaylinesGoToCase(caseType: string): void {
        if (this.paylines != null) {
            this.paylines.goToCase(caseType);
        }
    }

    protected winPaylineGoToCase(winIndex: number, caseType: string): void {
        if (this.paylines != null) {
            const win = this.currentWins[winIndex];

            if (win.trigger === Communication.Game.WinTrigger.Payline) {
                if (caseType === Payline.CaseCycle && this.currentWins.length === 1) {
                    this.paylines.goToCase(Payline.CaseSingleCycle, win.paylineId);
                } else {
                    this.paylines.goToCase(caseType, win.paylineId);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  ANIMATIONS
    //******************************************************************************************************************

    protected createAnimations(): void {
        this.animationsContainer = new ContainerElement();
        this.animationsContainer.name = "Animations";

        this.addChild(this.animationsContainer);

        if (this.childs["Value"]) {
            this.value = this.childs["Value"];

            this.animationsContainer.addChild(this.value);
        }

        this.animations = [];

        for (let i = 0; i < this.animationNames.length; i++) {
            if (this.childs[this.animationNames[i]]) {
                let animation = this.childs[this.animationNames[i]];
                animation.visible = false;
                animation.on("finished", this.onAnimationFinished.bind(this));
                animation.on(TimelineElement.AnimationComplete, this.onAnimationComplete.bind(this));

                this.animations.push(animation);

                this.animationsContainer.addChild(animation);
            }
        }

        this.currentAnimationIndex = null;
        this.currentAnimationFinished = true;
    }

    protected resetAnimations(): void {
        this.currentAnimationIndex = null;
        this.currentAnimationFinished = true;

        for (let i = 0; i < this.animations.length; i++) {
            const animation = this.animations[i];
            animation.visible = false;
            animation.stop();
        }
    }

    protected startAnimation(): void {
        this.currentAnimationIndex = this.getAnimationIndex();
        this.currentAnimationFinished = false;

        const animation = this.animations[this.currentAnimationIndex];
        animation.visible = true;

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()));
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(this.currentWin);
        }
    }

    protected stopAnimation(): void {
        if (this.currentAnimationIndex != null) {
            const animation = this.animations[this.currentAnimationIndex];
            animation.visible = true;
            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                animation.goToOutro(parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()));
            } else {
                ImageTextCounterElement.Round = false;
                animation.goToOutro(this.currentWin);
            }

            this.currentAnimationIndex = null;
            this.currentAnimationFinished = true;
        }
    }

    protected getAnimationIndex(): number {
        return this.currentMultiplierIndex;
    }

    protected onAnimationFinished(): void {
        this.currentAnimationFinished = true;

        this.emit(WinAnimator.FinishWinCalculation);
    }

    protected onAnimationComplete(): void {
        this.currentAnimationIndex = null;

        this.finishFirstWins();
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {
        for (let i = 0; i < this.getIdleActiveElementsCount(); i++) {
            const symbol =
                this.currentSymbols[Math.floor(Math.random() * this.reelCount)][
                    Math.floor(Math.random() * this.symbolCount)
                ];

            if (symbol.getCurrentCase() === Symbol.CaseNormal) {
                if (symbol.hasCase(Symbol.CaseIdle)) {
                    symbol.goToCase(Symbol.CaseIdle);
                }
            }
        }

        this.startTimer(WinAnimator.Idle, this.getIdleDelay());
    }

    protected getInitialIdleDelay(): number {
        return 120;
    }

    protected getIdleDelay(): number {
        return 120;
    }

    protected getIdleActiveElementsCount(): number {
        return 2;
    }
}

export const WinAnimatorAttributes = {
    /**
     * Name of config for win animator.
     * @property Configs
     * @type string
     * @default null
     */
    CONFIGS: "Configs",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/WinAnimator.ts
 **/